// src/components/common/BottomSheet.tsx
import React, { useEffect, useRef } from 'react';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  showHeader?: boolean;
}

/**
 * 하단 팝업 시트 (공통 컴포넌트)
 * - 시대선택 등 하단에서 올라오는 팝업
 * - Tailwind CSS + tw-animate-css 사용
 */
const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  children,
  showHeader = false,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* 오버레이 */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/50 z-[998] transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* 시트 컨테이너 */}
      <div
        ref={sheetRef}
        className={`fixed left-0 right-0 bottom-0 bg-white rounded-t-[20px] max-h-[70vh] z-[999] shadow-[0_-4px_16px_rgba(0,0,0,0.15)] transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {/* 헤더 */}
        {showHeader && (
          <div className="flex justify-center items-center px-6 pt-5 pb-4 border-b border-gray-200">
            {title && <h3 className="text-lg font-bold text-gray-800 m-0">{title}</h3>}
            <button
              onClick={onClose}
              className="bg-transparent absolute right-[10px] border-none text-2xl text-gray-500 cursor-pointer p-1 flex items-center justify-center w-8 h-8 rounded-full transition-colors duration-200 hover:bg-gray-100 active:scale-95"
            >
              ✕
            </button>
          </div>
        )}

        {/* 콘텐츠 */}
        <div className="px-6 py-5 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 scrollbar-thumb-rounded-full scrollbar-track-rounded-full">
          {children}
        </div>
      </div>
    </>
  );
};

export default BottomSheet;
